/**
 * Cross-Dashboard Bridge
 * ─────────────────────
 * Bidirectional event bus between the Individual Dashboard and Team Dashboard.
 *
 * Uses BroadcastChannel for real-time cross-tab delivery and localStorage for
 * persistence (the last 20 events survive page refreshes).
 *
 * Both dashboards import this same module. Events flow symmetrically:
 *   Individual ──► "individual:*"  ──► Team Dashboard reads
 *   Team       ──► "team:*"        ──► Individual Dashboard reads
 */

export type BridgeEventType =
  // ── Individual → Team ──────────────────────────────────────
  | 'individual:active'         // user opened / is on individual dashboard
  | 'individual:section_changed'// user navigated to a new section
  | 'individual:tool_used'      // user launched an AI tool
  | 'individual:idle'           // user left individual dashboard
  // ── Team → Individual ──────────────────────────────────────
  | 'team:project_assigned'     // current user was assigned to a project
  | 'team:new_commit'           // new commit on a connected project
  | 'team:member_joined'        // a new member joined the workspace
  | 'team:budget_alert'         // budget threshold crossed
  | 'team:notification'         // generic team notification
  | 'team:status_update';       // project status changed

export interface BridgeEvent {
  id: string;
  type: BridgeEventType;
  /** Human-readable short label (used in notification toasts) */
  title: string;
  body: string;
  /** ISO timestamp */
  at: string;
  /** Optional structured payload for consumers that want more detail */
  payload?: Record<string, unknown>;
}

// ─── Storage & channel keys ────────────────────────────────────────────────
const CHANNEL_NAME   = 'scrudix_dashboard_bridge';
const STORAGE_KEY    = 'scrudix_bridge_events';
const MAX_PERSISTED  = 20;

// ─── Listeners ────────────────────────────────────────────────────────────
type BridgeListener = (event: BridgeEvent) => void;
const listeners = new Set<BridgeListener>();

// ─── BroadcastChannel (one global instance) ───────────────────────────────
let channel: BroadcastChannel | null = null;
let channelReady = false;

function getChannel(): BroadcastChannel | null {
  if (!channelReady && typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (ev: MessageEvent<BridgeEvent>) => {
      if (ev.data && ev.data.type) {
        notifyListeners(ev.data);
      }
    };
    channelReady = true;
  }
  return channel;
}

function notifyListeners(event: BridgeEvent) {
  listeners.forEach(fn => {
    try { fn(event); } catch { /* ignore per-listener errors */ }
  });
}

// ─── Persistence helpers ──────────────────────────────────────────────────
function readPersisted(): BridgeEvent[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as BridgeEvent[]) : [];
  } catch {
    return [];
  }
}

function writePersisted(events: BridgeEvent[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events.slice(0, MAX_PERSISTED)));
  } catch { /* quota errors are non-fatal */ }
}

// ─── Public API ──────────────────────────────────────────────────────────

/**
 * Emit an event from either dashboard.
 * Immediately persists to localStorage (cross-session) and broadcasts to
 * other open tabs via BroadcastChannel (real-time).
 */
export function emitBridgeEvent(
  type: BridgeEventType,
  title: string,
  body: string,
  payload?: Record<string, unknown>
): BridgeEvent {
  const event: BridgeEvent = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    type,
    title,
    body,
    at: new Date().toISOString(),
    payload,
  };

  // Persist
  const existing = readPersisted();
  writePersisted([event, ...existing]);

  // Broadcast to other tabs
  try {
    getChannel()?.postMessage(event);
  } catch { /* channel may be closed */ }

  // Also notify same-tab listeners (useful when both dashboards are on the
  // same route via <Outlet> / embedded views, or in the same JS bundle).
  notifyListeners(event);

  return event;
}

/**
 * Subscribe to bridge events.
 * @param filter  Optional event type prefix ('team:' or 'individual:') or exact type
 * @returns unsubscribe function
 */
export function onBridgeEvent(
  handler: BridgeListener,
  filter?: string
): () => void {
  const wrapped: BridgeListener = (ev) => {
    if (!filter || ev.type.startsWith(filter) || ev.type === filter) {
      handler(ev);
    }
  };
  listeners.add(wrapped);
  // Ensure channel is open so we receive cross-tab messages
  getChannel();
  return () => listeners.delete(wrapped);
}

/**
 * Get the N most recent persisted events, optionally filtered by prefix.
 */
export function getRecentBridgeEvents(limit = 10, filter?: string): BridgeEvent[] {
  const all = readPersisted();
  const filtered = filter ? all.filter(e => e.type.startsWith(filter)) : all;
  return filtered.slice(0, limit);
}

/**
 * Clear all persisted bridge events (useful on logout).
 */
export function clearBridgeEvents() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}
